import { Box, Button, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CalendarIcon } from '../resources/images/calendar.svg';
import { ReactComponent as CheckboxIcon } from '../resources/images/checkbox.svg';
import { ReactComponent as CheckedCheckboxIcon } from '../resources/images/checked-checkbox.svg';
import { ReactComponent as InfoIcon } from '../resources/images/info-icon.svg';
import Util from '../Util';
import TimeFrameDates from '../wizard/TimeFrameDates';
import './DateSelectionForm.scss';

interface DateSelectionFormProps {
    onSubmit: Function;
    onCancel: Function;
    actionTitle: string;
    actionDescription: string;
    config?: any;
}

const DateSelectionForm = (props: DateSelectionFormProps) => {
    const { t } = useTranslation();
    const [start_date, setStartDate] = useState<Date | null>(null);
    const [end_date, setEndDate] = useState<Date | null>(null);
    const [can_confirm_dates, setCanConfirmDates] = useState<boolean>(false);
    const [isPartial, setIsPartial] = useState<boolean>(props.actionTitle !== 'Pull data'); // Pull data -> partial always off

    function validateDates(startDate: Date, endDate: Date) {
        const is_valid = endDate >= startDate;
        setCanConfirmDates(is_valid);
    }

    function timeFrameDatesExists() {
        return !!(
            props.config?.environments[0]?.processed_files?.adapter_output?.adapter_output_from &&
            props.config?.environments[0]?.processed_files?.adapter_output?.adapter_output_to
        );
    }

    function getWarningContent() {
        const oldStart = new Date(props.config?.environments[0]?.processed_files?.adapter_output?.adapter_output_from);
        const oldEnd = new Date(props.config?.environments[0]?.processed_files?.adapter_output?.adapter_output_to);

        let content = '';

        const convertDateToString = (date: Date) => date?.toLocaleDateString('en-GB');

        const oldStartStr = convertDateToString(oldStart);
        const oldEndStr = convertDateToString(oldEnd);
        const start_date_str = convertDateToString(start_date);
        const end_date_str = convertDateToString(end_date);

        if (start_date <= oldStart && end_date >= oldEnd) {
            content = 'The complete data will be retrieved and will replace the current dataset.';
        } else if (start_date <= oldStart && end_date < oldEnd) {
            content = `The complete data will be retrieved and will replace the current dataset. Data pertaining to events occurring between ${end_date_str} and ${oldEndStr} will be lost.`;
        } else if (start_date > oldStart && end_date <= oldEnd) {
            content = `The complete data will be retrieved and will replace the current dataset. Data pertaining to events occurring between ${oldStartStr} and ${start_date_str}, and between ${end_date_str} and ${oldEndStr} will be lost.`;
        } else if (start_date > oldStart && end_date > oldEnd) {
            content = `The complete data will be retrieved and will replace the current dataset. Data pertaining to events occurring between ${oldStartStr} and ${start_date_str} will be lost.`;
        }

        return (
            <Typography style={{ color: '#4A4A4A' }} fontSize="14px" fontStyle="italic">
                {content}
            </Typography>
        );
    }

    return (
        <Stack className="DateSelectionForm" direction="column">
            <Stack
                style={{
                    background: '#f2f3f4',
                    padding: '16px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                }}
            >
                {timeFrameDatesExists() && (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            marginBottom="16px"
                            color="#4A4A4A"
                            letterSpacing="0.04em"
                        >
                            <InfoIcon />
                            <Typography marginLeft="8px" fontSize="14px">
                                Dataset time frame currently in use:
                            </Typography>
                            <Typography marginLeft="12px" fontStyle="italic">
                                <TimeFrameDates
                                    from={
                                        props.config?.environments[0]?.processed_files?.adapter_output
                                            ?.adapter_output_from
                                    }
                                    to={
                                        props.config?.environments[0]?.processed_files?.adapter_output
                                            ?.adapter_output_to
                                    }
                                />
                            </Typography>
                        </Stack>
                        <Divider style={{ marginBottom: '16px' }} />
                    </>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom="16px">
                    <Typography style={{ color: '#4A4A4A' }} letterSpacing="0.04em" lineHeight="140%" fontSize="16px">
                        {`When should the ${props.actionDescription}`} <b style={{ fontWeight: 500 }}>start?</b>
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <MobileDatePicker
                            //label={t("config-list.select-start-date")}
                            value={start_date}
                            inputFormat="MM/dd/yyyy"
                            onChange={(newValue) => {
                                setStartDate(newValue);
                                validateDates(newValue, end_date);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" style={{ paddingRight: '12px' }}>
                                        <CalendarIcon />
                                    </InputAdornment>
                                ),
                                style: {
                                    color: '#616264',
                                    background: 'white',
                                    fontSize: '13px',
                                    height: '38px',
                                    width: '190px',
                                    borderRadius: '6px',
                                },
                                placeholder: 'Pick a day',
                            }}
                            renderInput={(params) => (
                                <TextField
                                    inputProps={{
                                        'data-testid': 'process-start-date',
                                    }}
                                    {...params}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography style={{ color: '#4A4A4A' }} letterSpacing="0.04em" lineHeight="140%" fontSize="16px">
                        {`When should the ${props.actionDescription}`} <b style={{ fontWeight: 500 }}>end?</b>
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <MobileDatePicker
                            //label={t("config-list.select-end-date")}
                            value={end_date}
                            inputFormat="MM/dd/yyyy"
                            onChange={(newValue) => {
                                setEndDate(newValue);
                                validateDates(start_date, newValue);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" style={{ paddingRight: '12px' }}>
                                        <CalendarIcon />
                                    </InputAdornment>
                                ),
                                style: {
                                    color: '#616264',
                                    background: 'white',
                                    fontSize: '13px',
                                    height: '38px',
                                    width: '190px',
                                    borderRadius: '6px',
                                },
                                placeholder: 'Pick a day',
                            }}
                            renderInput={(params) => (
                                <TextField
                                    inputProps={{
                                        'data-testid': 'process-end-date',
                                    }}
                                    {...params}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Stack>
            </Stack>

            {props.actionTitle !== 'Pull data' && props.actionTitle !== 'Test template' && (
                <Stack
                    direction="row"
                    alignItems="center"
                    marginBottom="20px"
                    onClick={() => setIsPartial((prev) => !prev)}
                    sx={{ cursor: 'pointer' }}
                >
                    <Checkbox
                        disableRipple
                        checkedIcon={<CheckedCheckboxIcon />}
                        icon={<CheckboxIcon />}
                        color="primary"
                        checked={!isPartial}
                        sx={{ padding: 0 }}
                    />
                    <Typography marginLeft="10px" fontSize="16px">
                        Override dataset time frame currently in use
                    </Typography>
                </Stack>
            )}

            {(props.actionTitle === 'Pull data' || !isPartial) &&
            props.actionTitle !== 'Test template' &&
            can_confirm_dates ? (
                <Stack direction="row" boxShadow="0px 0px 10px 0px rgba(196, 196, 196, 0.25)" marginBottom="40px">
                    <Box width="8px" height="156px" borderRadius="4px 0px 0px 4px" sx={{ background: '#ED4D4D' }} />
                    <Stack padding="12px" width="100%">
                        <Typography fontWeight="500" fontSize="14px" letterSpacing="0.56px">
                            Are you sure?
                        </Typography>
                        <Divider style={{ marginBottom: '12px', marginTop: '12px' }} />
                        <Stack direction="row" marginBottom="8px">
                            <Typography marginRight="7px" fontWeight="300" fontSize="14px" letterSpacing="0.56px">
                                New dataset time frame:
                            </Typography>
                            <Typography fontSize="14px" letterSpacing="0.56px">
                                {Util.formatLongDate(new Date(start_date))} - {Util.formatLongDate(new Date(end_date))}
                            </Typography>
                        </Stack>
                        {getWarningContent()}
                    </Stack>
                </Stack>
            ) : null}

            <Stack direction="row" justifyContent="flex-end">
                <Button style={{ marginRight: '15px' }} onClick={() => props.onCancel()} variant="text">
                    Cancel
                </Button>
                <Button
                    disabled={!can_confirm_dates}
                    onClick={() => props.onSubmit(start_date, end_date, isPartial)}
                    variant="contained"
                    style={{
                        padding: '8px 24px',
                        fontWeight: 400,
                        borderRadius: '6px',
                        lineHeight: '18px',
                        boxShadow: 'none',
                    }}
                    data-testid="process-dates-submit-btn"
                >
                    {props.actionTitle === 'Refresh data' ? 'Process & Refresh' : props.actionTitle}
                </Button>
            </Stack>
        </Stack>
    );
};

export default DateSelectionForm;
